import { useState } from "react";
import { CurrencyFormat } from "../../../utils";
import useConfiguratorData from "../../hooks/useConfiguratorData";
import useAccessoriesData from "../../hooks/useAccessoriesData";
import usePurchaseTimeframeData from "../../hooks/usePurchaseTimeframeData";
import useLeadData from "../../hooks/useLeadData";
import usePersonalData from "../../hooks/usePersonalData";
import usePaymentOptions from "../../hooks/usePaymentOptions";
import useKintoOnePersonalData from "../../hooks/useKintoOnePersonalData";
import useMAFSimulationData from "../../hooks/useMAFSimulationData";
import ModalCustomMessage from "../Modals/ModalCustomMessage";
import ModalContact from "../Modals/ModalContactMe";
import Portal from "../Portal";
import { SmallCheckIcon } from "../Icons/SmallCheckIcon";
import QuoteDetail from "./QuoteDetail";

/**
 * Resumen fijo del vehículo configurado (versión nueva v2)
 * Muestra información del modelo y maneja la cotización
 */
const FixedSummary = ({ onQuoteClick, isHidden = false }) => {
    const { getSelectedModel, getSelectedVersion, getSelectedPrepaidMaintenancePlan } = useConfiguratorData();
    const { getAddedAccessories } = useAccessoriesData();
    const { getSelectedTimeframe } = usePurchaseTimeframeData();
    const {
        updateLead,
        sendFinancingSummaryEmail,
        createSalesforceLead,
        updateSalesforceLead,
        getSalesforceLeadId,
    } = useLeadData();
    const { getPersonalData } = usePersonalData();
    const { getSelectedOption, isCash, isMAF, isKinto } = usePaymentOptions();
    const { getCurrentKintoData } = useKintoOnePersonalData();
    const { ensureSimulation } = useMAFSimulationData();

    const selectedModel = getSelectedModel();
    const selectedVersion = getSelectedVersion();
    const selectedPrepaidMaintenancePlan = getSelectedPrepaidMaintenancePlan();
    const selectedTimeframe = getSelectedTimeframe();
    const accessories = getAddedAccessories();
    const personalData = getPersonalData();
    const salesforceLeadId = getSalesforceLeadId();
    const selectedPaymentOption = getSelectedOption();
    const kintoOnePersonalData = getCurrentKintoData();

    // Estado para controlar el modal de confirmación
    const [modalCustomMessageIsOpen, setModalCustomMessageIsOpen] = useState(false);
    // Estado para controlar el modal de contacto
    const [modalContactIsOpen, setModalContactIsOpen] = useState(false);
    // Aviso cuando no se pudo registrar la cotización
    const [modalErrorIsOpen, setModalErrorIsOpen] = useState(false);
    // Evita reenvíos mientras la simulación o el guardado están en curso
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Despliegue del detalle de configuración sobre la barra fija
    const [detailIsOpen, setDetailIsOpen] = useState(false);

    // Cálculo de precios adicionales (igual que en Summary.js)
    const accessoriesPrice = accessories.length > 0 
        ? accessories.reduce((total, accessory) => total + (accessory.price || 0), 0) 
        : 0;
    
    const maintenancePlanPrice = selectedPrepaidMaintenancePlan && !isKinto && !isCash 
        ? selectedPrepaidMaintenancePlan.price || 0 
        : 0;

    // Cálculo de precio dinámico según opción de pago
    const calculateDynamicPrice = () => {
        // Precio base (list_price - ya incluye todos los bonos aplicados)
        let basePrice = selectedVersion.price;
        
        // Para Kinto: precio sin bonos (list_price original)
        if (isKinto) {
            basePrice = selectedVersion.list_price;
        }
        // Para Contado: precio con solo bono marca
        else if (isCash) {
            basePrice = selectedVersion.list_price - (selectedVersion.brand_bonus || 0);
        }
        // Para MAF: precio con todos los bonos (bono marca + financiamiento + plan renueve)
        // Este es el precio que ya viene en version.price
        
        return basePrice;
    };

    const dynamicPrice = calculateDynamicPrice();

    // Si no hay modelo o versión seleccionada, no renderizar
    if (!selectedModel || !selectedVersion) {
        return null;
    }

    // Verificar si tiene bonificaciones disponibles según la opción de pago
    // Kinto: No aplican bonos
    // Contado: Solo bono marca
    // MAF: Bono marca + bono financiamiento + plan renueve (si aplica)
    const hasBonus = !isKinto && (
        selectedVersion.brand_bonus ||
        (selectedVersion.financing_bonus && isMAF) ||
        (selectedVersion.smart_buy_bonus && isMAF)
    );

    // Maneja el clic en el botón (lógica condicional igual que Summary.js)
    const handleQuoteClick = async () => {
        // Guarda contra doble click: sin esto se generan dos update_lead y,
        // como salesforceLeadId todavía es null en ambos, dos leads en Salesforce
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            await submitQuote();
        } catch (error) {
            // Un fallo inesperado no puede dejar el botón deshabilitado para siempre
            console.error("Error al enviar la cotización:", error);
            setIsSubmitting(false);
            setModalErrorIsOpen(true);
        }
    };

    const submitQuote = async () => {
        // Si es MAF, asegurar que la simulación exista y corresponda a la
        // configuración actual antes de guardar el lead. getLeadData lee el
        // estado más reciente, por lo que no hace falta esperar de más.
        if (isMAF) {
            await ensureSimulation();
        }

        // Determinar si el usuario quiere contacto basado en selectedTimeframe
        const wantsContact = selectedTimeframe && selectedTimeframe.id !== null ? "Si" : "No";

        // Actualizar el lead con la preferencia de contacto
        updateLead(
            [
                {
                    name: "quiero-que-me-contacten",
                    value: wantsContact,
                },
            ],
            () => {
                setIsSubmitting(false);

                // Si quiere contacto, mostrar modal de contacto
                // Si no quiere contacto (solo cotizando), enviar email y mostrar mensaje de agradecimiento
                if (wantsContact === "Si") {
                    // Para FixedSummary, mostramos el modal de contacto directamente
                    setModalContactIsOpen(true);
                } else {
                    // Enviar email de financing summary para "Solo estoy cotizando"
                    sendFinancingSummaryEmail();
                    setModalCustomMessageIsOpen(true);
                }

                if(dataLayer) {
                    dataLayer.push({
                        'event': 'cotizacion_enviada',
                        'eventCategory': 'Cotizador',
                        'eventAction': 'sticky',
                        'eventLabel': 'enviar_cotizacion',
                        'motor_type': selectedVersion.motor.name.replace(/\s+/g, '_').toLowerCase(),
                        'modelo': selectedModel.name.replace(/\s+/g, '_').toLowerCase(),
                        'version': selectedVersion.name.replace(/\s+/g, '_').toLowerCase(),
                        'opcion_pago': selectedPaymentOption.name.replace(/\s+/g, '_').toLowerCase(),
                    });
                    
                }

                // Crear o actualizar lead de Salesforce según corresponda
                if (!salesforceLeadId) {
                    createSalesforceLead([
                        {
                            name: "quiero-que-me-contacten",
                            value: wantsContact,
                        },
                    ]);
                } else {
                    updateSalesforceLead([
                        {
                            name: "quiero-que-me-contacten",
                            value: wantsContact,
                        },
                    ]);
                }
            },
            () => {
                // El lead no se guardó: no se puede mostrar el flujo de éxito,
                // porque el cliente se iría creyendo que su cotización quedó
                // registrada. Se avisa del fallo y se permite reintentar.
                setIsSubmitting(false);
                setModalErrorIsOpen(true);
            }
        );
    };

    // Función para manejar el cierre del modal de contacto sin elegir opción
    const handleContactModalClose = () => {
        // Cerrar el modal de contacto
        setModalContactIsOpen(false);
        
        // Actualizar el lead indicando que NO quiere contacto
        updateLead(
            [
                {
                    name: "quiero-que-me-contacten",
                    value: "No",
                },
            ],
            () => {
                // Mostrar el modal de agradecimiento
                setModalCustomMessageIsOpen(true);
                
                // Actualizar o crear lead de Salesforce
                if (!salesforceLeadId) {
                    createSalesforceLead([
                        {
                            name: "quiero-que-me-contacten",
                            value: "No",
                        },
                    ]);
                } else {
                    updateSalesforceLead([
                        {
                            name: "quiero-que-me-contacten",
                            value: "No",
                        },
                    ]);
                }
            },
            () => {
                // Mismo criterio: si no se pudo actualizar el lead, se avisa
                setModalErrorIsOpen(true);
            }
        );
    };

    return (
        <div className={`fixed-summary ${isHidden ? "hidden" : ""}${detailIsOpen ? " expanded" : ""}`}>
            {detailIsOpen && <QuoteDetail onClose={() => setDetailIsOpen(false)} />}
            <div className="fixed-summary-content">
                <div className="vehicle-info">
                    <h3 className="vehicle-name">
                        {selectedModel.name} {selectedVersion.name}
                        {accessories.length > 0 ? ` + ${accessories.length} accesorios` : ""}
                        {selectedPrepaidMaintenancePlan && !isKinto && !isCash ? ` + mantenciones` : ""}
                    </h3>
                    <div className="price-info" style={isKinto? { flexDirection: "row" } : {} }>
                        {isKinto && kintoOnePersonalData && kintoOnePersonalData?.api_response ? (
                            <>
                                {CurrencyFormat(
                                    Number(
                                        kintoOnePersonalData?.api_response?.amortization[
                                            kintoOnePersonalData?.api_response.amortization.length - 1
                                        ].totalQuota
                                    )
                                )}
                                <span className="kinto-monthly-quote">Cuota Mensual KINTO ONE</span>
                            </>
                        ) : (
                            <>
                                {CurrencyFormat(dynamicPrice)}
                                {(accessoriesPrice > 0 || maintenancePlanPrice > 0) && (
                                    <div className="additional-prices">
                                        {accessoriesPrice > 0 && (
                                            <small>+ Accesorios: {CurrencyFormat(accessoriesPrice)}</small>
                                        )}
                                        {maintenancePlanPrice > 0 && (
                                            <small>+ Mantenciones: {CurrencyFormat(maintenancePlanPrice)}</small>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className={"bonus-info" + (!hasBonus ? " no-bonus" : "")}>
                    <div className="bonus-title">
                        <img
                            src={`${window._env_.STATIC_HOST}/static/website/images/icons/bonus-new.svg`}
                            alt="Bonus icon"
                        />
                        <span>Este precio incluye:</span>
                    </div>
                    <ul className="bonus-list">
                        <li className="bonus-item">
                            <SmallCheckIcon />{" "}
                            <strong>
                                {isMAF && "Pago con financiamiento MaF"}
                                {isKinto && "Servicios KINTO One"}
                                {isCash && "Pago contado"}
                            </strong>
                        </li>
                        {selectedVersion.brand_bonus && !isKinto && (
                            <li className="bonus-item">
                                <SmallCheckIcon /> Bono <strong>marca</strong> de{" "}
                                {CurrencyFormat(selectedVersion.brand_bonus)}.
                            </li>
                        )}
                        {selectedVersion.financing_bonus && isMAF && (
                            <li className="bonus-item">
                                <SmallCheckIcon /> Bono <strong>financiamiento MAF</strong> de{" "}
                                {CurrencyFormat(selectedVersion.financing_bonus)}.
                            </li>
                        )}
                        {selectedVersion.smart_buy_bonus && isMAF && (
                            <li className="bonus-item">
                                <SmallCheckIcon /> Bono{" "}
                                <strong>financiamiento Plan Renueve</strong> de{" "}
                                {CurrencyFormat(selectedVersion.smart_buy_bonus)}.
                            </li>
                        )}
                    </ul>
                </div>

                <div className="box">
                    <button
                        className={"quote-button" + (isSubmitting ? " loading" : "")}
                        onClick={handleQuoteClick}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Enviando..." : "Enviar cotización"}
                    </button>

                    <button
                        type="button"
                        className={"detail-toggle" + (detailIsOpen ? " open" : "")}
                        onClick={() => setDetailIsOpen(!detailIsOpen)}
                        aria-expanded={detailIsOpen}
                    >
                        Ver detalle de tu cotización
                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                            <path
                                d="M1 1.5L6 6.5L11 1.5"
                                stroke="#191919"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            <Portal>
                <ModalContact
                    isOpen={modalContactIsOpen}
                    onCloseModal={handleContactModalClose}
                />
            </Portal>
            
            <Portal>
                <ModalCustomMessage
                    isOpen={modalCustomMessageIsOpen}
                    onCloseModal={() => setModalCustomMessageIsOpen(false)}
                    title="¡Muchas Gracias!"
                    message={`Gracias por tu interés en ${selectedModel.name} ${selectedVersion.name}.`}
                    textButton="¡Listo!"
                />
            </Portal>

            <Portal>
                <ModalCustomMessage
                    isOpen={modalErrorIsOpen}
                    onCloseModal={() => setModalErrorIsOpen(false)}
                    redirectOnClose={false}
                    title="No pudimos enviar tu cotización"
                    message="Hubo un problema al registrar tu cotización. Por favor, inténtalo nuevamente en unos minutos."
                    textButton="Reintentar"
                />
            </Portal>
        </div>
    );
};

export default FixedSummary;
